import { useCallback, useContext, useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Divider,
  Grid,
  MenuItem,
  Slider,
  Stack,
  TextField,
  Typography,
} from '@mui/material'
import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import SaveIcon from '@mui/icons-material/Save'
import CallSplitIcon from '@mui/icons-material/CallSplit'

import { Context } from '../../../context/AuthContext'
import PageHeader from '../../Shared/PageHeader'
import SelectSubCompany from '../../Shared/SelectSubCompany'
import LoadingBackdrop from '../../Shared/LoadingBackdrop'
import { useFeedback } from '../../Shared/FeedbackSnackbar'
import {
  createOrder,
  searchClients as buscarClientes,
} from '../../../services/productionOrderServices'
import { searchProductService } from '../../../services/productService'
import { getProcess } from '../../../services/processServices'
import { mensagemDeErro } from '../../../services/apiClient'
import { conversaoDoProduto, dividirQuantidade, numeroDaQuebra } from '../../../utils/quebras'
import { formatNumber } from '../../../utils/format'
import useDebounce from '../../../utils/useDebounce'

const EMPTY_FORM = {
  numberOtherSystem: '',
  product: null,
  client: null,
  processId: '',
  quantityTotal: '',
  scheduledFor: '',
  responsibleName: '',
  observation: '',
  breakCount: 1,
}

const MAX_PREVIEW = 12

const ProductionOrderCreate = () => {
  const { subCompany, userName } = useContext(Context)
  const navigate = useNavigate()
  const { notify, FeedbackSnackbar } = useFeedback()

  const [subCompanyId, setSubCompanyId] = useState(
    subCompany.length === 1 ? subCompany[0].subCompanyId.trim() : ''
  )
  const [form, setForm] = useState({ ...EMPTY_FORM, responsibleName: userName || '' })
  const [saving, setSaving] = useState(false)

  const [processes, setProcesses] = useState([])
  const [products, setProducts] = useState([])
  const [productTerm, setProductTerm] = useState('')
  const [searchingProducts, setSearchingProducts] = useState(false)

  const [clients, setClients] = useState([])
  const [clientTerm, setClientTerm] = useState('')
  const [searchingClients, setSearchingClients] = useState(false)

  /** Insumos além do produto final. A ordem produz o produto principal. */
  const [extras, setExtras] = useState([])
  const [extraProduct, setExtraProduct] = useState(null)
  const [extraQuantity, setExtraQuantity] = useState('')

  const changeForm = (field, value) => setForm(current => ({ ...current, [field]: value }))

  const fetchProcesses = useCallback(async () => {
    if (!subCompanyId) {
      setProcesses([])
      return
    }
    try {
      const response = await getProcess({ subCompanyId })
      setProcesses(response.data.result || [])
    } catch (error) {
      setProcesses([])
      notify(mensagemDeErro(error, 'Não foi possível carregar os fluxos de produção.'), 'error')
    }
  }, [subCompanyId, notify])

  useEffect(() => {
    fetchProcesses()
  }, [fetchProcesses])

  // O fluxo pertence à filial; trocar de filial invalida o que estava escolhido.
  useEffect(() => {
    changeForm('processId', '')
  }, [subCompanyId])

  const searchProducts = useCallback(async () => {
    setSearchingProducts(true)
    try {
      const response = await searchProductService({
        SearchWord: productTerm || undefined,
        limit: 20,
        offset: 0,
      })
      setProducts(response.data.result || [])
    } catch (error) {
      setProducts([])
    } finally {
      setSearchingProducts(false)
    }
  }, [productTerm])

  useDebounce(() => searchProducts(), 400, [productTerm])

  const searchClients = useCallback(async () => {
    if (!subCompanyId) {
      setClients([])
      return
    }
    setSearchingClients(true)
    try {
      const response = await buscarClientes({ subCompanyId, search: clientTerm })
      setClients(response.data.result || [])
    } catch (error) {
      setClients([])
    } finally {
      setSearchingClients(false)
    }
  }, [subCompanyId, clientTerm])

  useDebounce(() => searchClients(), 400, [clientTerm, subCompanyId])

  // Cliente pertence à filial: trocar de filial invalida quem estava escolhido.
  useEffect(() => {
    changeForm('client', null)
  }, [subCompanyId])

  const quantidade = Number(form.quantityTotal) || 0
  const fracoes = Number(form.breakCount) || 0

  /**
   * Prévia da divisão. A autoridade é a API — este cálculo existe só para o
   * usuário ver antes de salvar que 100 em 3 vira 34/33/33, e não 33/33/33 com
   * uma peça sumindo, que era o comportamento do sistema antigo.
   */
  const previewQuebras = useMemo(() => {
    if (quantidade <= 0 || fracoes <= 0) return []
    const numeroBase = form.numberOtherSystem || 'OP'
    return dividirQuantidade(quantidade, fracoes).map((quantidadeDaQuebra, indice) => ({
      numero: numeroDaQuebra(numeroBase, indice + 1, fracoes),
      quantidade: quantidadeDaQuebra,
    }))
  }, [quantidade, fracoes, form.numberOtherSystem])

  const fracaoInvalida = fracoes > quantidade && quantidade > 0

  /**
   * O único teto real é a quantidade da ordem: a API recusa com 409 qualquer
   * divisão que deixe uma quebra com zero peça. Números redondos como 50 ou
   * 1000 só impediam divisões legítimas de ordens grandes.
   */
  const maxQuebras = quantidade > 0 ? quantidade : undefined

  // O slider é atalho para os casos comuns; passou disso, arrastar deixa de ser
  // prático e quem manda é o campo numérico.
  const maxSlider = Math.max(10, Math.min(maxQuebras || 10, 50))

  /** Fator de conversão do produto — orienta o número de quebras, nada mais. */
  const conversao = useMemo(
    () => conversaoDoProduto(quantidade, form.product),
    [quantidade, form.product]
  )

  const handleSave = async () => {
    if (!subCompanyId) {
      notify('Selecione a filial da ordem.', 'warning')
      return
    }
    if (!form.numberOtherSystem.trim()) {
      notify('Informe o número da ordem no sistema de origem.', 'warning')
      return
    }
    if (quantidade < 1) {
      notify('A quantidade total precisa ser de ao menos uma peça.', 'warning')
      return
    }
    if (fracaoInvalida) {
      notify('Há mais quebras do que peças: alguma delas ficaria com quantidade zero.', 'warning')
      return
    }

    setSaving(true)
    try {
      // O produto final vai como primeiro item — é dele que a API tira o
      // produto e a quantidade da ordem. Os extras entram como insumo.
      const items = form.product
        ? [
            { productId: form.product.id, quantity: quantidade, finishGood: true },
            ...extras.map(extra => ({
              productId: extra.product.id,
              quantity: extra.quantity,
              finishGood: false,
            })),
          ]
        : undefined

      const response = await createOrder({
        subCompanyId,
        numberOtherSystem: form.numberOtherSystem.trim(),
        productId: form.product?.id || undefined,
        clientId: form.client?.id || undefined,
        items,
        processId: form.processId || undefined,
        quantityTotal: quantidade,
        scheduledFor: form.scheduledFor || undefined,
        responsibleName: form.responsibleName.trim() || undefined,
        observation: form.observation.trim() || undefined,
        breakCount: fracoes > 0 ? fracoes : undefined,
      })
      notify('Ordem de produção criada com sucesso!', 'success')
      navigate(`/MovBreak/${response.data.id}`)
    } catch (error) {
      notify(mensagemDeErro(error, 'Não foi possível criar a ordem de produção.'), 'error')
    } finally {
      setSaving(false)
    }
  }

  return (
    <Box>
      <PageHeader
        title="Criar Ordem de Produção"
        subtitle="A ordem pode já nascer fracionada em quebras — é a quebra que o chão de fábrica aponta."
        actions={
          <>
            <Button
              variant="outlined"
              startIcon={<ArrowBackIcon />}
              onClick={() => navigate('/MovBreak')}
            >
              Voltar
            </Button>
            <Button
              variant="contained"
              startIcon={<SaveIcon />}
              disabled={saving}
              onClick={handleSave}
            >
              Salvar
            </Button>
          </>
        }
      />

      <Card variant="outlined" sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 2 }}>
            Dados da ordem
          </Typography>

          <Grid container spacing={2}>
            <Grid item xs={12} md={4}>
              <SelectSubCompany
                size="small"
                value={subCompanyId}
                onChange={event => setSubCompanyId(event.target.value)}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <TextField
                fullWidth
                required
                size="small"
                label="Número da ordem (sistema de origem)"
                value={form.numberOtherSystem}
                onChange={event => changeForm('numberOtherSystem', event.target.value)}
                helperText="Único por filial — repetir devolve erro."
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <TextField
                select
                fullWidth
                size="small"
                label="Processo (fluxo)"
                value={form.processId}
                disabled={!subCompanyId}
                onChange={event => changeForm('processId', event.target.value)}
                helperText={
                  subCompanyId ? 'Define as etapas do Kanban.' : 'Escolha a filial primeiro.'
                }
              >
                <MenuItem value="">Sem fluxo definido</MenuItem>
                {processes.map(processo => (
                  <MenuItem key={processo.id} value={processo.id}>
                    {processo.name}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>

            <Grid item xs={12} md={6}>
              <Autocomplete
                size="small"
                options={products}
                value={form.product}
                loading={searchingProducts}
                loadingText="Procurando..."
                filterOptions={opcoes => opcoes}
                getOptionLabel={option =>
                  [option.name, option.reference, option.sku].filter(Boolean).join(' · ')
                }
                isOptionEqualToValue={(option, selected) => option.id === selected.id}
                onInputChange={(event, valor) => setProductTerm(valor)}
                onChange={(event, valor) => changeForm('product', valor)}
                noOptionsText="Nenhum produto encontrado"
                renderInput={params => (
                  <TextField {...params} label="Produto final" helperText="O que a ordem produz." />
                )}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <Autocomplete
                size="small"
                options={clients}
                value={form.client}
                loading={searchingClients}
                loadingText="Procurando..."
                disabled={!subCompanyId}
                filterOptions={opcoes => opcoes}
                getOptionLabel={option => [option.codClient, option.name].filter(Boolean).join(' - ')}
                isOptionEqualToValue={(option, selected) => option.id === selected.id}
                onInputChange={(event, valor) => setClientTerm(valor)}
                onChange={(event, valor) => changeForm('client', valor)}
                noOptionsText="Nenhum cliente encontrado"
                renderInput={params => (
                  <TextField
                    {...params}
                    label="Cliente"
                    helperText={
                      subCompanyId
                        ? 'Opcional. Busca por código, nome ou documento.'
                        : 'Escolha a filial primeiro.'
                    }
                  />
                )}
              />
            </Grid>
            <Grid item xs={6} sm={3} md={2}>
              <TextField
                fullWidth
                required
                size="small"
                type="number"
                label="Quantidade total"
                inputProps={{ min: 1 }}
                value={form.quantityTotal}
                onChange={event => changeForm('quantityTotal', event.target.value)}
              />
            </Grid>
            <Grid item xs={6} sm={3} md={2}>
              <TextField
                fullWidth
                size="small"
                type="date"
                label="Data planejada"
                InputLabelProps={{ shrink: true }}
                value={form.scheduledFor}
                onChange={event => changeForm('scheduledFor', event.target.value)}
              />
            </Grid>
            <Grid item xs={12} md={2}>
              <TextField
                fullWidth
                size="small"
                label="Responsável"
                value={form.responsibleName}
                onChange={event => changeForm('responsibleName', event.target.value)}
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                minRows={2}
                size="small"
                label="Observações"
                value={form.observation}
                onChange={event => changeForm('observation', event.target.value)}
              />
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      <Card variant="outlined" sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 0.5 }}>
            Outros produtos da ordem
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Insumos que entram na ordem além do produto final. Opcional — quase toda ordem tem só
            o produto final.
          </Typography>

          <Grid container spacing={2} alignItems="flex-start">
            <Grid item xs={12} md={6}>
              <Autocomplete
                size="small"
                options={products}
                value={extraProduct}
                loading={searchingProducts}
                loadingText="Procurando..."
                filterOptions={opcoes => opcoes}
                getOptionLabel={option =>
                  [option.name, option.reference, option.sku].filter(Boolean).join(' · ')
                }
                isOptionEqualToValue={(option, selected) => option.id === selected.id}
                onInputChange={(event, valor) => setProductTerm(valor)}
                onChange={(event, valor) => setExtraProduct(valor)}
                noOptionsText="Nenhum produto encontrado"
                renderInput={params => <TextField {...params} label="Produto" />}
              />
            </Grid>
            <Grid item xs={6} sm={4} md={3}>
              <TextField
                fullWidth
                size="small"
                type="number"
                label="Quantidade"
                inputProps={{ min: 1 }}
                value={extraQuantity}
                onChange={event => setExtraQuantity(event.target.value)}
              />
            </Grid>
            <Grid item xs={6} sm={4} md={3}>
              <Button
                fullWidth
                variant="outlined"
                disabled={!extraProduct || Number(extraQuantity) < 1}
                onClick={() => {
                  if (extraProduct.id === form.product?.id) {
                    notify('Este já é o produto final da ordem.', 'warning')
                    return
                  }
                  if (extras.some(item => item.product.id === extraProduct.id)) {
                    notify('Este produto já está na lista.', 'warning')
                    return
                  }
                  setExtras(atual => [
                    ...atual,
                    { product: extraProduct, quantity: Number(extraQuantity) },
                  ])
                  setExtraProduct(null)
                  setExtraQuantity('')
                }}
              >
                Adicionar
              </Button>
            </Grid>
          </Grid>

          {extras.length > 0 && (
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 2 }}>
              {extras.map(item => (
                <Chip
                  key={item.product.id}
                  variant="outlined"
                  label={`${item.product.name} · ${formatNumber(item.quantity)}`}
                  onDelete={() =>
                    setExtras(atual => atual.filter(linha => linha.product.id !== item.product.id))
                  }
                />
              ))}
            </Box>
          )}
        </CardContent>
      </Card>

      <Card variant="outlined">
        <CardContent>
          <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
            <CallSplitIcon color="action" />
            <Typography variant="subtitle1" fontWeight={600}>
              Fracionamento da produção
            </Typography>
          </Stack>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Em quantas quebras a quantidade será dividida. Cada quebra é uma unidade de
            acompanhamento própria — tem status, etapa atual e apontamento de RFID.
          </Typography>

          {conversao?.packages > 0 && (
            <Alert
              severity="info"
              sx={{ mb: 2 }}
              action={
                <Button
                  size="small"
                  disabled={Number(form.breakCount) === conversao.packages}
                  onClick={() => changeForm('breakCount', conversao.packages)}
                >
                  Usar {conversao.packages}
                </Button>
              }
            >
              {`Este produto é contado em ${conversao.unitLabel.toLowerCase()} de `}
              <strong>{formatNumber(conversao.piece)}</strong>
              {` peças: ${formatNumber(quantidade)} peças dão `}
              <strong>{conversao.packages}</strong>
              {` ${conversao.packages === 1 ? 'embalagem' : 'embalagens'}`}
              {quantidade % conversao.piece !== 0 && ' (a última incompleta)'}
              {'. A quantidade das quebras continua em peças.'}
            </Alert>
          )}

          <Grid container spacing={3} alignItems="center">
            <Grid item xs={12} sm={4} md={3}>
              <TextField
                fullWidth
                size="small"
                type="number"
                label="Quantidade de quebras"
                inputProps={{ min: 0, max: maxQuebras }}
                value={form.breakCount}
                onChange={event => changeForm('breakCount', event.target.value)}
                error={fracaoInvalida}
                helperText={
                  fracaoInvalida
                    ? `No máximo ${formatNumber(quantidade)} — uma por peça.`
                    : '0 cria a ordem sem quebras.'
                }
              />
            </Grid>
            <Grid item xs={12} sm={8} md={5}>
              <Slider
                min={0}
                max={maxSlider}
                value={Math.min(fracoes, maxSlider)}
                marks={maxSlider <= 20}
                valueLabelDisplay="auto"
                onChange={(event, valor) => changeForm('breakCount', valor)}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              {quantidade > 0 && fracoes > 0 && !fracaoInvalida && (
                <Typography variant="body2" color="text.secondary">
                  {formatNumber(quantidade)} peças em {fracoes} quebra(s) de{' '}
                  <strong>
                    {Math.min(...previewQuebras.map(item => item.quantidade))}
                    {previewQuebras.some(
                      item => item.quantidade !== previewQuebras[0].quantidade
                    ) && ` a ${Math.max(...previewQuebras.map(item => item.quantidade))}`}
                  </strong>{' '}
                  peças.
                </Typography>
              )}
            </Grid>
          </Grid>

          {fracaoInvalida && (
            <Alert severity="warning" sx={{ mt: 2 }}>
              {`Não dá para dividir ${formatNumber(quantidade)} peça(s) em ${fracoes} quebras: `}
              alguma ficaria com quantidade zero.
            </Alert>
          )}

          {previewQuebras.length > 0 && !fracaoInvalida && (
            <>
              <Divider sx={{ my: 2 }} />
              <Typography variant="caption" color="text.secondary">
                Prévia das quebras que serão criadas
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 1 }}>
                {previewQuebras.slice(0, MAX_PREVIEW).map(quebra => (
                  <Chip
                    key={quebra.numero}
                    size="small"
                    variant="outlined"
                    label={`${quebra.numero} · ${formatNumber(quebra.quantidade)}`}
                  />
                ))}
                {previewQuebras.length > MAX_PREVIEW && (
                  <Chip
                    size="small"
                    color="primary"
                    label={`+${previewQuebras.length - MAX_PREVIEW} quebras`}
                  />
                )}
              </Box>
            </>
          )}
        </CardContent>
      </Card>

      <LoadingBackdrop open={saving} message="Criando a ordem..." />
      <FeedbackSnackbar />
    </Box>
  )
}

export default ProductionOrderCreate
